import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FindAllCommunitySpendQuery,
  FindAllCommunitySpendQueryVariables,
} from "../../../../schemaTypes";
import { WithFindAllMyCommunitySpend } from "../../Merchant/FindAllMyCommunitySpend";

export const findAllCommunitySpendQuery = gql`
  query FindAllCommunitySpend(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCommunitySpend(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        community {
          id
          name
          alias
          logoUrl
          isArchive
          createdBy {
            id
            admin{
                id
                level
                firstname
                lastname
            }
          }
          createdAt
          updatedAt
        }
        totalSpend
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

interface Props {
  variables: FindAllCommunitySpendQueryVariables;
  children: (data: WithFindAllMyCommunitySpend) => React.ReactElement | null;
}

export const FindAllCommunitySpend: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCommunitySpendQuery,
    FindAllCommunitySpendQueryVariables
  >(findAllCommunitySpendQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const pageData = data?.findAllCommunitySpend || {
    data: [],
    total: 0,
    page: variables.page,
    pageSize: variables.pageSize,
    totalPages: 0,
  };

  const communitiesWithSpend = pageData.data;
  const page = pageData.page;
  const pageSize = pageData.pageSize;
  const total = pageData.total;
  const totalPages = pageData.totalPages;
  const hasMore = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllCommunitySpend) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCommunitySpend: {
            ...fetchMoreResult.findAllCommunitySpend,
            data: [
              ...(prev?.findAllCommunitySpend?.data || []),
              ...(fetchMoreResult.findAllCommunitySpend.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMore]);

  return children({
    communitiesWithSpend: communitiesWithSpend as any[],
    loading,
    loadMore,
    hasMore,
    page,
    pageSize,
    total,
    totalPages,
  });
};
